/**
 * Helper utilities for worker communication
 */

import { WORKER_CONFIG, WORKER_ERROR_MESSAGES } from '../constants/worker';

/**
 * Check if an error indicates a worker method is not available
 */
export function isWorkerMethodError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return Object.values(WORKER_ERROR_MESSAGES).some(
    (errorMsg) => message.includes(errorMsg.toLowerCase())
  );
}

/**
 * Sleep utility for delays
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = WORKER_CONFIG.MAX_LOAD_RETRIES,
  baseDelay: number = WORKER_CONFIG.METHOD_CALL_DELAY,
  onRetry?: (attempt: number, error: unknown) => void
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt < maxRetries) {
        onRetry?.(attempt + 1, error);
        const delay = baseDelay * (attempt + 1); // Exponential backoff
        await sleep(delay);
      }
    }
  }

  throw lastError;
}

